import { ImageAutoComplete } from "@components/auto-completes/image";
import { Controller, useFormContext } from "react-hook-form";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { useEffect, useState } from "react";
import { UserLOV } from "@models/users/entities/user";
import { useEffectOnce } from "react-use";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { useIsNew } from "@hooks/useIsNew";
import { useUserPermissions } from "@hooks/userUserPermission";

export const AnalysisSetupOwnerField = ({
  userType,
  disabled,
  isSupplierUser = userType === "supplier",
}: AnalysisSetupStepRenderedFieldProps) => {
  const isNew = useIsNew();
  const { user, isAdviserSupportStaffGroup } = useUserPermissions();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AnalysisSetupDTO>();
  const { setBannerName, getPlatformAnalysisOwners, getAdviserUsers } =
    usePlatformAnalysis();
  const { supplierData } = useSelector((state: RootState) => state.supplier);

  const [ownerData, setOwnerData] = useState<UserLOV[]>([]);

  const handleOnFieldChange = (userLov: UserLOV | null) => {
    const { name, avatarUrl, userType } = userLov || {};
    setValue("ownerName", name ?? "", { shouldDirty: true });
    setValue("ownerAvatarUrl", avatarUrl ?? "", { shouldDirty: true });
    setValue("ownerType", userType ?? "", { shouldDirty: true });
    setValue("familyGroupId", undefined);
    setValue("familyGroupName", undefined);
    setBannerName({ ownerName: name, ownerType: userType });
  };

  useEffectOnce(() => {
    if (isSupplierUser) {
      getPlatformAnalysisOwners().then((data) => {
        setOwnerData(data ?? []);
      });

      return;
    }

    getAdviserUsers("new", watch("ownerName")).then((data) => {
      setOwnerData(data ?? []);
    });
  });

  useEffect(() => {
    if (!supplierData || !isSupplierUser) return;
    setValue("supplierId", supplierData.id);
    setValue("supplierName", supplierData.companyName);
    setBannerName({ supplierName: supplierData.companyName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierData]);

  useEffect(() => {
    if (!isNew || !user || isAdviserSupportStaffGroup) return;
    const { auth0Id, name, avatarUrl, userType } = user || {};
    setValue("ownerId", auth0Id ?? "");
    setValue("ownerName", name ?? "");
    setValue("ownerAvatarUrl", avatarUrl ?? "");
    setValue("ownerType", userType ?? "");
    setBannerName({ ownerName: name, ownerType: userType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isNew]);

  return (
    <Controller
      name="ownerId"
      control={control}
      render={({ field: { onChange } }) => (
        <ImageAutoComplete
          sx={{ width: "100%" }}
          textFieldProps={{
            label: isSupplierUser ? "Owner" : "Adviser",
            placeholder: `Select an ${isSupplierUser ? "owner" : "adviser"}`,
            helperText: errors?.ownerId?.message,
            error: !!errors.ownerId,
            required: true,
          }}
          value={{
            auth0Id: watch("ownerId"),
            name: watch("ownerName"),
            avatarUrl: watch("ownerAvatarUrl"),
          }}
          isOptionEqualToValue={(option, value) =>
            option.auth0Id === value.auth0Id
          }
          onChange={(_, newValue) => {
            onChange(newValue?.auth0Id);
            handleOnFieldChange(newValue);
          }}
          labelField={"name"}
          imageField={"avatarUrl"}
          options={ownerData}
          disabled={disabled}
        />
      )}
    />
  );
};
